/**
 * Free note limit: the legacy-device marker.
 *
 * From `NOTE_LIMIT_CUTOFF` on, a device with no account is capped at
 * `FREE_NOTE_LIMIT` active notes (see `hooks/useNoteLimit.ts`). Devices that
 * were already writing notes before that day keep unlimited local notes, the
 * same way the server grandfathers every account created before the cutoff.
 * Since a local-mode device has no server row, the only place that memory can
 * live is the device itself: this localStorage marker.
 *
 * On every launch before the cutoff, `markLegacyDeviceIfEligible` stamps the
 * marker when the local DB already holds a note or the device ever entered
 * local mode. After the cutoff nothing writes it any more, so only devices
 * marked in time stay exempt.
 *
 * A device that does not open the PWA during that window is not forgotten: on
 * its first launch after the cutoff (once, tracked by the `evaluated` key) the
 * notes themselves are the witness, and a note created before the cutoff
 * still stamps the marker. That read is local SQLite only.
 *
 * Scope: the marker is per device, not per account. A device that held notes
 * before the cutoff while signed in is marked too; the server verdict wins
 * while an account is signed in, and the marker only matters if that device
 * later runs local mode. Same physical device, so this is deliberate.
 *
 * Accepted trade-off: a reinstall (or cleared site data) loses the marker and
 * the device becomes a "new" free device. Signing in restores the server's
 * verdict, which is the durable one.
 */

import { NOTE_LIMIT_CUTOFF } from '@caliu/types'
import { queryOne } from './sqlite'
import { LOCAL_MODE_KEY } from '../store/auth'

export const NOTE_LIMIT_LEGACY_KEY = 'caliu-note-limit-legacy'
/** Set once the post-cutoff evaluation has run, so it runs exactly once. */
export const NOTE_LIMIT_EVALUATED_KEY = 'caliu-note-limit-legacy-evaluated'

/** Pure rule behind the marker. `now` and `cutoff` are epoch milliseconds. */
export function shouldMarkLegacy({
  now,
  cutoff,
  hasNotes,
  hasLocalModeKey,
}: {
  now: number
  cutoff: number
  hasNotes: boolean
  hasLocalModeKey: boolean
}): boolean {
  if (now >= cutoff) return false
  return hasNotes || hasLocalModeKey
}

/** True when this device was stamped as pre-cutoff. Storage failures read as
 *  "not legacy": the limit then applies, which is the conservative answer. */
export function isLegacyDevice(): boolean {
  try {
    return localStorage.getItem(NOTE_LIMIT_LEGACY_KEY) === 'true'
  } catch {
    return false
  }
}

/**
 * Runs once per app launch, right after the local DB opens (`main.tsx`). Cheap:
 * one `LIMIT 1` query and two localStorage reads. Idempotent.
 */
export function markLegacyDeviceIfEligible(now: number = Date.now()): void {
  if (isLegacyDevice()) return
  const cutoff = Date.parse(NOTE_LIMIT_CUTOFF)
  if (now >= cutoff) {
    markLegacyFromPreCutoffNotesOnce()
    return
  }
  let hasLocalModeKey = false
  try {
    hasLocalModeKey = localStorage.getItem(LOCAL_MODE_KEY) != null
  } catch {
    // Storage unavailable: the DB check below still decides.
  }
  const hasNotes = queryOne<{ one: number }>('SELECT 1 AS one FROM notes LIMIT 1') != null
  if (!shouldMarkLegacy({ now, cutoff, hasNotes, hasLocalModeKey })) return
  setLegacyMarker()
}

/**
 * After the cutoff, a device that missed the pre-cutoff window is judged once
 * by its own rows: any note created before the cutoff proves the device was
 * writing before day D. Runs exactly once per install (`evaluated` key), so a
 * pre-cutoff note synced in later cannot retroactively exempt a new device.
 * Note rows store `created_at` as ISO 8601, the same shape as the constant.
 */
function markLegacyFromPreCutoffNotesOnce(): void {
  try {
    if (localStorage.getItem(NOTE_LIMIT_EVALUATED_KEY) === 'true') return
  } catch {
    return // cannot record the evaluation: do not decide on this launch
  }
  const witness = queryOne<{ one: number }>(
    'SELECT 1 AS one FROM notes WHERE created_at < ? LIMIT 1',
    [NOTE_LIMIT_CUTOFF],
  )
  try {
    if (witness != null) localStorage.setItem(NOTE_LIMIT_LEGACY_KEY, 'true')
    localStorage.setItem(NOTE_LIMIT_EVALUATED_KEY, 'true')
  } catch {
    // Storage unavailable: nothing to persist; the next launch retries.
  }
}

function setLegacyMarker(): void {
  try {
    localStorage.setItem(NOTE_LIMIT_LEGACY_KEY, 'true')
  } catch {
    // Storage unavailable: nothing to persist; the next launch retries.
  }
}
